import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, Search, ShoppingBag, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: 0, category: "", is_active: true });

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data.products);
    } catch (e) { toast.error("Error al cargar productos"); }
    finally { setIsLoading(false); }
  };

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setForm({ name: "", description: "", price: 0, category: "", is_active: true });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingProduct(p);
    setForm({ name: p.name, description: p.description || "", price: p.price || 0, category: p.category || "", is_active: p.is_active !== false });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setIsSaving(true);
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/admin/products`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Producto creado");
      }
      setIsFormOpen(false);
      fetchProducts();
    } catch (e) { toast.error("Error al guardar"); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Producto eliminado");
      fetchProducts();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="products-page">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="products-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-rose-600" /> Productos & Servicios
              </h1>
              <p className="text-xs text-stone-500">{products.length} productos</p>
            </div>
          </div>
          <Button onClick={handleOpenCreate} className="bg-rose-600 hover:bg-rose-700" data-testid="create-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Buscar por nombre o categoría..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white" />
        </div>

        {isLoading ? <div className="text-center py-12 text-stone-500">Cargando...</div> :
        filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 text-lg mb-2">No hay productos</p>
            <Button onClick={handleOpenCreate} variant="outline"><Plus className="w-4 h-4 mr-2" /> Crear primer producto</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(p => (
              <Card key={p.id} className={`border-l-4 ${p.is_active !== false ? 'border-l-rose-500' : 'border-l-stone-300 opacity-60'}`} data-testid={`product-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-stone-900">{p.name}</h3>
                      {p.category && <Badge variant="secondary" className="text-xs mt-1">{p.category}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(p)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(p.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {p.description && <p className="text-sm text-stone-500 mb-2">{p.description}</p>}
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-rose-600" />
                    <span className="text-xl font-bold text-rose-600">{(p.price || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> {editingProduct ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
            <DialogDescription>Los productos aparecerán como opciones en Pedidos y Facturas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: 1000 Business Cards" data-testid="product-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Detalles del producto..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Precio ($)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} step="0.01" min="0" data-testid="product-form-price" />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Ej: Impresión, Digital..." />
              </div>
            </div>
            {editingProduct && (
              <div className="flex items-center justify-between">
                <Label>Activo</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-rose-600 hover:bg-rose-700" data-testid="product-save-btn">
              {isSaving ? "Guardando..." : (editingProduct ? "Guardar" : "Crear Producto")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
